import { getLaAreas } from '../services/laAreas/laAreaService';
import { getSites } from '../services/sites/sitesService';
import { getEnabledAccounts, getEnabledMasterAccounts } from '../services/accounts/accountsService';
import { getTreatments } from '../services/treatments/treatmentsService';
import { getVatRates } from '../services/vatRates/vatRatesService';
import { getEwcs } from '../services/ewcs/ewcsService';
import { getFormulations } from '../services/formulations/formulationsService';
import { getHauliersEnabled } from '../services/hauliers/hauliersService';
import { getEnabledCommodities } from '../services/commodities/commoditiesService';
import { getVehicleTypes } from '../services/vehicleTypes/vehicleTypesService';
import { getContractsEnabled } from '../services/contracts/contractsService';
import { getTransTypesEnabled } from '../services/transTypes/transTypesService';
import { getDriversEnabled } from '../services/drivers/driversService';
import { getLocationsEnabled } from '../services/locations/locationsService';
import { getShipsEnabled } from '../services/ships/shipsService';
import { getTrailersEnabled } from '../services/trailers/trailersService';
import { getVehiclesEnabled } from '../services/vehicles/vehiclesService';
import { AUDIT_FIELD_KEYS } from './auditFieldConstants';

const AUDIT_FIELDS = AUDIT_FIELD_KEYS;

const LOCATION_AVAILABLE_FIELDS = [
    'erpCode', 'name', 'shortName', 'locationType', 'email',
    'address1', 'address2', 'address3', 'address4', 'postCode',
    'enabled', 'notInUse', 'quarantine',
    'balance', 'capacity', 'hatch',
    'accountId', 'commodityId', 'shipId', 'siteId',
    'account.name', 'commodity.name', 'commodity.erpCode',
    'ship.description', 'site.description',
    ...AUDIT_FIELDS,
];

const VEHICLE_AVAILABLE_FIELDS = [
    'badge', 'description', 'discriminator', 'email', 'maxWt', 'mobile',
    'siteId', 'site.description', 'stopped', 'stoppedReason', 'tareId',
    'vehicleReg', 'vehicleTypeId', 'vehicleType.description',
    ...AUDIT_FIELDS,
];

export const lookupConfig = {
    laAreaId: {
        // Advanced / form lookups search all legacy fields (code, name, address1–4)
        fetch: (opts) => getLaAreas({ ...opts, searchAll: true }),
        displayField: ['name'],
        fields: ['name', 'address1', 'address2', 'address3', 'address4'],
        availableFields: ['name', 'code', 'region', 'email', 'phone', 'fax', 'address1', 'address2', 'address3', 'address4'],
    },

    siteId: {
        fetch: getSites,
        displayField: ['description'],
        fields: ['description', 'code'],
        availableFields: ['description', 'code'],
    },

    assAccountId: {
        fetch: getEnabledMasterAccounts,
        displayField: ['name'],
        fields: ['name', 'shortName', 'erpCode', 'address1', 'address2', 'address3', 'address4', 'email', 'accountType'],
        availableFields: [
            'name', 'shortName', 'email', 'erpCode', 'enabled',
            'address1', 'address2', 'address3', 'address4', 'accountType',
        ],
    },

    accountId: {
        fetch: getEnabledAccounts,
        displayField: ['name'],
        fields: ['name', 'address1', 'address2', 'address3', 'address4', 'email'],
        availableFields: [
            'name', 'shortName', 'email', 'enabled', 'stopped', 'stoppedReason',
            'accountType', 'address1', 'address2', 'address3', 'address4',
            'assAccount.name', 'erpCode', 'domestic', 'accInvNo',
            'laArea.name', 'site.description',
            'minimumBalance', 'paymentType', 'isCa', 'postCode',
            'prepaidBalance', 'prepaidComment', 'qualityNumber',
            'currency', 'vatExempt', 'vatNumber', 'usedCount', 'operation',
            ...AUDIT_FIELDS,
        ],
    },

    treatmentId: {
        fetch: getTreatments,
        displayField: ['description'],
        fields: ['description'],
        availableFields: ['code', 'description', 'price', ...AUDIT_FIELDS],
    },

    vatRateId: {
        fetch: getVatRates,
        displayField: ['description'],
        fields: ['vatRate', 'description', 'inclusive'],
        availableFields: ['description', 'vatRate', 'enabled', 'inclusive', ...AUDIT_FIELDS],
    },

    ewcId: {
        fetch: getEwcs,
        displayField: ['code'],
        fields: ['code', 'description'],
        availableFields: ['code', 'description', ...AUDIT_FIELDS],
    },

    formulationId: {
        fetch: getFormulations,
        displayField: ['description'],
        fields: ['code', 'description'],
        availableFields: ['code', 'description', ...AUDIT_FIELDS],
    },

    haulierId: {
        fetch: getHauliersEnabled,
        displayField: ['name'],
        fields: ['name', 'address1', 'address2', 'address3'],
        availableFields: [
            'erpCode', 'name', 'shortName', 'email',
            'address1', 'address2', 'address3', 'address4', 'postCode',
            'permitNumber', 'enabled', 'stopped', 'stoppedReason',
            'siteId', 'site.description',
            ...AUDIT_FIELDS,
        ],
    },

    commodityId: {
        fetch: (params) => getEnabledCommodities(params),
        displayField: ['name'],
        fields: ['name'],
        availableFields: [
            'name', 'shortName', 'erpCode', 'enabled',
            'account.name', 'siteId', 'site.description',
            'chargeByLoad', 'commodityGroup', 'conversionFactor',
            'ewcId', 'formulationId', 'minPrice', 'price',
            'treatmentId', 'units', 'vatRateId', 'isCa', 'usedCount',
            'priceNew', 'priceUpdateDate',
            ...AUDIT_FIELDS,
        ],
    },

    vehicleTypeId: {
        fetch: getVehicleTypes,
        displayField: ['description'],
        fields: ['description'],
        availableFields: ['description'],
    },

    masterContractId: {
        fetch: getContractsEnabled,
        displayField: 'description',
        fields: ['ref', 'description'],
        availableFields: [
            'ref', 'description', 'erpCode',
            'contractDate', 'deliveryDate',
            'price', 'minPrice', 'quantity',
            'enabled', 'singleLoad', 'chargePerLoad', 'status',
            'notes', 'weighed',
            'accountId', 'commodityId', 'driverId', 'haulierId',
            'laAreaId', 'locationId', 'masterContractId',
            'trailerId', 'transTypeId', 'vehicleId', 'siteId',
            'site.description', 'account.name', 'commodity.name',
            'haulier.name', 'driver.name', 'location.name', 'laArea.name',
            'vehicle.vehicleReg', 'trailer.vehicleReg', 'masterContract.description',
            ...AUDIT_FIELDS,
        ],
    },

    transTypeId: {
        fetch: getTransTypesEnabled,
        displayField: ['description'],
        fields: ['description', 'iox'],
        availableFields: [
            'description', 'enabled', 'iox',
            'genAgri', 'genPort', 'genWaste', 'genWs',
            ...AUDIT_FIELDS,
        ],
    },

    driverId: {
        fetch: (params) => getDriversEnabled(params),
        displayField: ['name'],
        fields: ['name'],
        availableFields: [
            'name', 'code', 'enabled', 'haulierId', 'haulier.name',
            ...AUDIT_FIELDS,
        ],
    },

    locationId: {
        fetch: (params) => getLocationsEnabled({ ...params, locationTypes: ['STORE'] }),
        displayField: ['name'],
        fields: ['name'],
        availableFields: LOCATION_AVAILABLE_FIELDS,
    },

    shipId: {
        fetch: getShipsEnabled,
        displayField: ['description'],
        fields: ['description', 'ref', 'origin'],
        availableFields: [
            'description', 'ref', 'origin', 'due', 'bol', 'enabled', 'finished',
            'account.name', 'commodity.name', 'site.description',
            ...AUDIT_FIELDS,
        ],
    },

    sourceLocationId: {
        title: 'Source Locations',
        displayField: ['name'],
        fields: ['name', 'erpCode'],
        availableFields: LOCATION_AVAILABLE_FIELDS,
        fetch: (params) => getLocationsEnabled({ ...params, locationTypes: ['STORE'] }),
    },

    destinationLocationId: {
        title: 'Destination Locations',
        displayField: ['name'],
        fields: ['name', 'erpCode'],
        availableFields: LOCATION_AVAILABLE_FIELDS,
        fetch: (params) => getLocationsEnabled({ ...params, locationTypes: ['STORE'] }),
    },

    trailerId: {
        fetch: getTrailersEnabled,
        displayField: ['vehicleReg'],
        fields: ['vehicleReg'],
        availableFields: VEHICLE_AVAILABLE_FIELDS,
    },

    vehicleId: {
        fetch: getVehiclesEnabled,
        displayField: ['vehicleReg'],
        fields: ['vehicleReg', 'description', 'badge'],
        availableFields: VEHICLE_AVAILABLE_FIELDS,
    },
};
